import { type RefObject, useEffect, useRef } from 'react'
import { useBodyScrollLock } from './useBodyScrollLock.js'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * `aria-modal="true"`를 단 오버레이 둘이 같은 규칙을 쓰게 한다.
 *
 * 라이트박스와 스냅 모달은 지금 서로 다르게 동작한다 — 한쪽만 배경을 잠그고
 * 한쪽만 Escape를 받는다. 둘 다 초점을 다이얼로그로 옮기지 않고, Tab을 가두지도
 * 않고, 닫을 때 원래 자리로 돌려주지도 않는다. 스크린리더나 키보드로 여는 하객은
 * 열린 다이얼로그 밖의 문서를 계속 훑게 된다.
 *
 * 돌려주는 ref를 다이얼로그 컨테이너에 걸고, 그 컨테이너에 `tabIndex={-1}`을 준다.
 */
export function useModalDialog(onClose: () => void): RefObject<HTMLDivElement | null> {
  const containerRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useBodyScrollLock()

  useEffect(() => {
    const container = containerRef.current
    const previouslyFocused = document.activeElement

    // 초점을 다이얼로그 안으로 옮긴다. 안에 초점 받을 것이 없으면 컨테이너 자신이 받는다.
    const first = container?.querySelector<HTMLElement>(FOCUSABLE)
    if (first) first.focus()
    else container?.focus()

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeRef.current()
        return
      }
      if (event.key !== 'Tab' || container === null) return

      const focusable = [...container.querySelectorAll<HTMLElement>(FOCUSABLE)]
      if (focusable.length === 0) {
        event.preventDefault()
        container.focus()
        return
      }
      const firstEl = focusable[0]!
      const lastEl = focusable[focusable.length - 1]!
      const active = document.activeElement
      if (event.shiftKey && (active === firstEl || active === container)) {
        event.preventDefault()
        lastEl.focus()
      } else if (!event.shiftKey && active === lastEl) {
        event.preventDefault()
        firstEl.focus()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      // 닫고 나면 열기 전에 있던 자리로 초점을 돌려준다 — 없어진 엘리먼트면 그냥 둔다.
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus()
      }
    }
  }, [])

  return containerRef
}
